class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');

      if (this.dataset.productType == 'assurance') {
        sessionStorage.setItem('assurance-removed', true);
        cartItems.updateQuantity(this.dataset.index, 0, this.dataset.productHandle, this.dataset.variantId, true);
      } else {
        cartItems.updateQuantity(this.dataset.index, 0, this.dataset.productHandle, this.dataset.variantId, false);
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('keypress', this.handleKeyPress.bind(this));

    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  handleKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  onChange(event) {
    const { name, value, dataset } = event.target;
    if (name === 'updates[]') {
      this.updateQuantity(dataset.index, value, dataset.productHandle, dataset.variantId, true);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  renderSections(parsedState, line) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });

    const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      cartDrawerWrapper
        ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
        : lineItem.querySelector(`[name="${name}"]`).focus();
    } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
      trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
    }
  }

  /**
   * Some products should only be purchases with other products.
   * These are rules help manage that when items removed from the cart.
   *
   * @function cartRules
   * @param {string} productHandle - The handle of the product.
   * @param {number} variantId - The ID of the product variant.
   * @returns {Object|null} An updates object containing the update rules, or null if no rules apply.
   */
  cartRules(productHandle, variantId) {
    let updates = {};
    switch (productHandle) {
      case 'build-a-box-1':
        updates[variantId] = 0; // Build A Box (has 5 variants)
        updates[41362809552958] = 0; // Functional Shots - 12-Pack
        return updates;
      case 'the-signature-reset':
        updates[variantId] = 0; // The Signature Reset (ID 39959712333886)
        updates[39963585118270] = 0; // Skinny Cleanse | The Signature Reset
        updates[39964818702398] = 0; // Welcome Package
        updates[39959731175486] = 0; // The Signature eCourse
        return updates;
      default:
        return null;
    }
  }

  /**
   * Updates the quantity of an item in the cart or removes multiple items based on cart rules.
   *
   * @async
   * @function updateQuantity
   * @param {number} line - The line number of the item in the cart.
   * @param {number} quantity - The new quantity for the item.
   * @param {string} productHandle - The handle of the product.
   * @param {number} variantId - The ID of the product variant.
   * @param {boolean} [isAssurance=false] - Whether the item is a shipping assurance product.
   * @returns {Promise<void>}
   *
   * @description
   * This function handles updating item quantities in the cart. It can either update a single item's quantity
   * or remove multiple related items based on predefined cart rules. It also handles updating the cart UI
   * and manages loading states during the update process.
   *
   * The function performs the following steps:
   * 1. Enables loading state for the affected cart line.
   * 2. Determines whether to use cart change or update route based on cart rules.
   * 3. Sends a fetch request to update the cart.
   * 4. Handles the response, including updating assurance and rendering updated cart sections.
   * 5. Manages error states and disables loading upon completion.
   */
  async updateQuantity(line, quantity, productHandle, variantId, isAssurance = false) {
    this.enableLoading(line);

    let route = routes.cart_change_url;
    let request = { line, quantity };

    if (quantity == 0 && productHandle && variantId) {
      const updates = this.cartRules(productHandle, variantId);
      if (updates !== null) {
        route = routes.cart_update_url;
        request = { updates: updates };
      }
    }

    fetch(`${route}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        ...request,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        await this.addAssurance('update', line);

        const parsedState = JSON.parse(state);
        this.renderSections(parsedState, line);

        if (isAssurance && line == 1 && quantity == 0) {
          if (window.location.pathname == '/cart') {
            location.reload();
          }
        }
      })
      .catch((error) => {
        console.error('Error updateQuantity:', error);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading();
      });
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement =
        document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      lineItemError.querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace(
        '[quantity]',
        quantityElement.value
      );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  async addAssurance(opType = 'add', line) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    let rs = await fetch(`${routes.cart_url}`, config);
    let cartData = await rs.json();

    if (cartData && cartData.items && cartData.items.length == 1 && cartData.items[0].product_type == 'assurance') {
      this.updateQuantity(1, 0);
    }

    let hasAssuranceInCart = cartData.items.find((itm) => itm.product_type == 'assurance'),
      routeUrl = routes.cart_add_url;

    var cartTotal = (cartData.items || []).reduce(function (cartTotal, item) {
      if (item.product_type !== 'assurance') {
        cartTotal += item.line_price;
      }
      return cartTotal;
    }, 0);

    if (cartTotal > 0) {
      var cartTotalAmount = cartTotal / 100;

      var applicableRange = (window.assurance.variants || []).find((variant) => {
        var priceRange = variant.title.replaceAll('$', '').split('-');
        // If condition is not met, variant title formating is wrong

        if (priceRange.length == 2 && cartTotalAmount >= +priceRange[0] && cartTotalAmount <= +priceRange[1]) {
          return priceRange.length == 2 && cartTotalAmount >= +priceRange[0] && cartTotalAmount <= +priceRange[1];
        } else if (priceRange.length == 1) {
          return priceRange.length == 1 && cartTotalAmount >= +priceRange[0];
        }
      });

      let fd = new FormData();

      if (!hasAssuranceInCart && opType == 'update') return;

      if (hasAssuranceInCart) {
        fd.append('id', hasAssuranceInCart.variant_id);
        fd.append('quantity', 0);
        config.body = fd;
        let removeResponse = await fetch(`${routes.cart_change_url}`, config);

        if (opType == 'remove') {
          removeResponse.json();
        }
      }

      if (opType == 'add' || opType == 'update') {
        fd.append('id', applicableRange.id);
        fd.append('quantity', 1);
        fd.append(
          'sections',
          this.getSectionsToRender().map((section) => section.section)
        );
        fd.append('sections_url', window.location.pathname);

        config.body = fd;

        fetch(routeUrl, config)
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);

            this.renderSections(parsedState, line);
          })
          .catch((error) => {
            console.error('Error addAssurance:', error);
            this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          })
          .finally(() => {
            this.disableLoading();
          });
      }
    }
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    const statusMesg = document.querySelectorAll(`#CartItem-${line} .status-message`);

    statusMesg.forEach((item) => item.setAttribute('aria-live', 'assertive'));

    let cartRmv = document.querySelector('.product-remove');
    if (cartRmv) {
      let cartRmvBtn = cartRmv.querySelector('.btn-drawer-remove');
      if (cartRmv) cartRmvBtn.querySelector('span').classList.add('hidden');
      if (cartRmv) cartRmvBtn.querySelector('.loading-overlay__spinner').classList.remove('hidden');
    }
    let plsminusBtn = document.querySelectorAll('.quantity__button');
    let addToCartBtn = document.querySelectorAll('.quick-add__submit ');

    if (plsminusBtn) {
      plsminusBtn.forEach((elem) => {
        elem.setAttribute('disabled', '');
      });
    }
    if (addToCartBtn) {
      addToCartBtn.forEach((elem) => {
        elem.setAttribute('aria-disabled', true);
      });
    }

    if (window.location.pathname == '/cart') {
      let cartremoveBtn = document.querySelectorAll('.qty-adjuster__remove');
      cartremoveBtn.forEach((elem) => {
        elem.setAttribute('aria-disabled', true);
      });
      let cartRow = document.querySelectorAll('.cart-item');
      cartRow.forEach((elem) => {
        elem.setAttribute('aria-disabled', true);
      });
    }

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const statusMesg = document.querySelectorAll('.status-message');

    statusMesg.forEach((item) => item.removeAttribute('aria-live'));

    let cartRmv = document.querySelector('.product-remove');
    if (cartRmv) {
      let cartRmvBtn = cartRmv.querySelector('.btn-drawer-remove');
      cartRmvBtn.querySelector('span').classList.remove('hidden');
      cartRmvBtn.querySelector('.loading-overlay__spinner').classList.add('hidden');
    }
    let plsminusBtn = document.querySelectorAll('.quantity__button');
    let addToCartBtn = document.querySelectorAll('.quick-add__submit ');
    if (plsminusBtn) {
      plsminusBtn.forEach((elem) => {
        // elem.setAttribute('aria-disabled', false);
        elem.removeAttribute('disabled');
      });
    }
    if (addToCartBtn) {
      addToCartBtn.forEach((elem) => {
        elem.setAttribute('aria-disabled', false);
      });
    }
    if (window.location.pathname == '/cart') {
      let cartremoveBtn = document.querySelectorAll('.qty-adjuster__remove');
      cartremoveBtn.forEach((elem) => {
        elem.setAttribute('aria-disabled', false);
      });
      let cartRow = document.querySelectorAll('.cart-item');
      cartRow.forEach((elem) => {
        elem.setAttribute('aria-disabled', false);
      });
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, 300)
        );
      }
    }
  );
}
